/**
 * Shopify Store Optimization - Custom JavaScript
 * File: assets/custom-optimizations.js
 * 
 * Features:
 * - Quick Add to Cart
 * - Product Quick View
 * - Lazy Loading
 * - Cart Drawer
 * - Search Autocomplete
 * - Sticky Header
 * - Recently Viewed Products
 */

// ===========================================
// 1. QUICK ADD TO CART FUNCTIONALITY
// ===========================================
class QuickAddToCart {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('quick-add-btn')) {
        e.preventDefault();
        const productId = e.target.dataset.productId;
        this.addToCart(productId, e.target);
      }
    });
  }

  async addToCart(productId, button) {
    const originalText = button.textContent;
    button.textContent = 'Adding...';
    button.disabled = true;

    try {
      const formData = {
        items: [{
          id: productId,
          quantity: 1
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        button.textContent = '✓ Added!';
        button.style.background = '#4CAF50';
        
        // Update cart count
        this.updateCartCount();
        
        // Show cart drawer
        this.showCartDrawer();
        
        setTimeout(() => {
          button.textContent = originalText;
          button.style.background = '';
          button.disabled = false;
        }, 2000);
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      button.textContent = 'Error - Try Again';
      button.style.background = '#f44336';
      console.error('Add to cart error:', error);
      
      setTimeout(() => {
        button.textContent = originalText;
        button.style.background = '';
        button.disabled = false;
      }, 2000);
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const cartCount = document.querySelector('.cart-count');
      if (cartCount) {
        cartCount.textContent = cart.item_count;
        cartCount.classList.add('updated');
        setTimeout(() => cartCount.classList.remove('updated'), 300);
      }
    } catch (error) {
      console.error('Update cart count error:', error);
    }
  }

  showCartDrawer() {
    const cartDrawer = document.querySelector('.cart-drawer');
    if (cartDrawer) {
      cartDrawer.classList.add('active');
      this.loadCartContents();
    }
  }

  async loadCartContents() {
    try {
      const response = await fetch('/cart?view=drawer');
      const html = await response.text();
      const cartContents = document.querySelector('.cart-drawer-contents');
      if (cartContents) {
        cartContents.innerHTML = html;
      }
    } catch (error) {
      console.error('Load cart error:', error);
    }
  }
}

// ===========================================
// 2. PRODUCT QUICK VIEW MODAL
// ===========================================
class QuickView {
  constructor() {
    this.modal = this.createModal();
    this.init();
  }

  createModal() {
    const modal = document.createElement('div');
    modal.className = 'quick-view-modal';
    modal.innerHTML = `
      <div class="quick-view-overlay"></div>
      <div class="quick-view-content">
        <button class="quick-view-close">&times;</button>
        <div class="quick-view-body"></div>
      </div>
    `;
    document.body.appendChild(modal);
    return modal;
  }

  init() {
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('quick-view-btn')) {
        e.preventDefault();
        const productUrl = e.target.dataset.productUrl;
        this.openQuickView(productUrl);
      }

      if (e.target.classList.contains('quick-view-close') || 
          e.target.classList.contains('quick-view-overlay')) {
        this.closeQuickView();
      }
    });
  }

  async openQuickView(productUrl) {
    this.modal.classList.add('active');
    const body = this.modal.querySelector('.quick-view-body');
    body.innerHTML = '<div class="loading">Loading...</div>';

    try {
      const response = await fetch(productUrl + '?view=quick-view');
      const html = await response.text();
      body.innerHTML = html;
    } catch (error) {
      body.innerHTML = '<p>Error loading product. Please try again.</p>';
      console.error('Quick view error:', error);
    }
  }

  closeQuickView() {
    this.modal.classList.remove('active');
  }
}

// ===========================================
// 3. SMART IMAGE LAZY LOADING
// ===========================================
class LazyLoadImages {
  constructor() {
    this.options = {
      root: null,
      rootMargin: '50px',
      threshold: 0.01
    };
    this.init();
  }

  init() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        this.handleIntersection.bind(this),
        this.options
      );

      this.observeImages();
    } else {
      // Fallback for older browsers
      this.loadAllImages();
    }
  }

  observeImages() {
    const images = document.querySelectorAll('img[loading="lazy"]');
    images.forEach(img => this.observer.observe(img));
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        this.loadImage(img);
        this.observer.unobserve(img);
      }
    });
  }

  loadImage(img) {
    if (img.dataset.src) {
      img.src = img.dataset.src;
    }
    if (img.dataset.srcset) {
      img.srcset = img.dataset.srcset;
    }
    img.classList.add('loaded');
  }

  loadAllImages() {
    const images = document.querySelectorAll('img[loading="lazy"]');
    images.forEach(img => this.loadImage(img));
  }
}

// ===========================================
// 4. STICKY HEADER ON SCROLL
// ===========================================
class StickyHeader {
  constructor() {
    this.header = document.querySelector('.site-header');
    this.scrollThreshold = 100;
    this.lastScroll = 0;
    this.init();
  }

  init() {
    if (!this.header) return;

    window.addEventListener('scroll', this.handleScroll.bind(this), {
      passive: true
    });
  }

  handleScroll() {
    const currentScroll = window.pageYOffset;

    if (currentScroll > this.scrollThreshold) {
      this.header.classList.add('sticky');
      
      // Hide on scroll down, show on scroll up
      if (currentScroll > this.lastScroll) {
        this.header.classList.add('hidden');
      } else {
        this.header.classList.remove('hidden');
      }
    } else {
      this.header.classList.remove('sticky', 'hidden');
    }

    this.lastScroll = currentScroll;
  }
}

// ===========================================
// 5. SEARCH AUTOCOMPLETE
// ===========================================
class SearchAutocomplete {
  constructor() {
    this.searchInput = document.querySelector('.search-input');
    this.searchResults = this.createResultsContainer();
    this.debounceTimer = null;
    this.init();
  }

  createResultsContainer() {
    const container = document.createElement('div');
    container.className = 'search-autocomplete';
    this.searchInput?.parentNode.appendChild(container);
    return container;
  }

  init() {
    if (!this.searchInput) return;

    this.searchInput.addEventListener('input', (e) => {
      clearTimeout(this.debounceTimer);
      const query = e.target.value.trim();

      if (query.length < 2) {
        this.searchResults.innerHTML = '';
        this.searchResults.classList.remove('active');
        return;
      }

      this.debounceTimer = setTimeout(() => {
        this.search(query);
      }, 300);
    });

    // Close on click outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.search-form')) {
        this.searchResults.classList.remove('active');
      }
    });
  }

  async search(query) {
    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`
      );
      const data = await response.json();
      this.displayResults(data.resources.results.products);
    } catch (error) {
      console.error('Search error:', error);
    }
  }

  displayResults(products) {
    if (!products || products.length === 0) {
      this.searchResults.innerHTML = '<div class="no-results">No products found</div>';
      this.searchResults.classList.add('active');
      return;
    }

    const html = products.map(product => `
      <a href="${product.url}" class="search-result-item">
        <img src="${product.featured_image?.url}" alt="${product.title}" />
        <div class="search-result-info">
          <h4>${product.title}</h4>
          <p class="price">${this.formatPrice(product.price)}</p>
        </div>
      </a>
    `).join('');

    this.searchResults.innerHTML = html;
    this.searchResults.classList.add('active');
  }

  formatPrice(price) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(price / 100);
  }
}

// ===========================================
// 6. RECENTLY VIEWED PRODUCTS
// ===========================================
class RecentlyViewed {
  constructor() {
    this.storageKey = 'recently_viewed_products';
    this.maxItems = 8;
    this.init();
  }

  init() {
    // Track current product
    const productId = this.getCurrentProductId();
    if (productId) {
      this.addProduct(productId);
    }

    // Display recently viewed
    this.displayRecentlyViewed();
  }

  getCurrentProductId() {
    const meta = document.querySelector('meta[property="og:url"]');
    if (meta && meta.content.includes('/products/')) {
      const match = meta.content.match(/\/products\/([^\/\?]+)/);
      return match ? match[1] : null;
    }
    return null;
  }

  addProduct(handle) {
    let products = this.getProducts();
    
    // Remove if already exists
    products = products.filter(p => p !== handle);
    
    // Add to beginning
    products.unshift(handle);
    
    // Keep only max items
    products = products.slice(0, this.maxItems);
    
    localStorage.setItem(this.storageKey, JSON.stringify(products));
  }

  getProducts() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey) || '[]');
    } catch {
      return [];
    }
  }

  async displayRecentlyViewed() {
    const container = document.querySelector('.recently-viewed-products');
    if (!container) return;

    const products = this.getProducts();
    if (products.length === 0) {
      container.style.display = 'none';
      return;
    }

    // Fetch product data
    const productData = await this.fetchProducts(products);
    this.renderProducts(container, productData);
  }

  async fetchProducts(handles) {
    try {
      const promises = handles.slice(0, 4).map(handle =>
        fetch(`/products/${handle}.js`).then(r => r.json())
      );
      return await Promise.all(promises);
    } catch (error) {
      console.error('Fetch recently viewed error:', error);
      return [];
    }
  }

  renderProducts(container, products) {
    const html = products.map(product => `
      <div class="recently-viewed-item">
        <a href="/products/${product.handle}">
          <img src="${product.featured_image}" alt="${product.title}" />
          <h4>${product.title}</h4>
          <p class="price">${this.formatPrice(product.price)}</p>
        </a>
      </div>
    `).join('');

    container.innerHTML = html;
  }

  formatPrice(price) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(price / 100);
  }
}

// ===========================================
// 7. CART DRAWER FUNCTIONALITY
// ===========================================
class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('.cart-drawer');
    this.init();
  }

  init() {
    if (!this.drawer) return;

    // Open cart drawer
    document.addEventListener('click', (e) => {
      if (e.target.closest('.cart-icon, .cart-link')) {
        e.preventDefault();
        this.open();
      }

      if (e.target.closest('.cart-drawer-close, .cart-drawer-overlay')) {
        this.close();
      }
    });

    // Update quantity
    this.drawer.addEventListener('change', (e) => {
      if (e.target.classList.contains('cart-quantity')) {
        this.updateQuantity(e.target);
      }
    });

    // Remove item
    this.drawer.addEventListener('click', (e) => {
      if (e.target.closest('.cart-remove')) {
        e.preventDefault();
        this.removeItem(e.target.closest('.cart-remove'));
      }
    });
  }

  async open() {
    this.drawer.classList.add('active');
    await this.refreshCart();
  }

  close() {
    this.drawer.classList.remove('active');
  }

  async refreshCart() {
    try {
      const response = await fetch('/cart?view=drawer');
      const html = await response.text();
      const contents = this.drawer.querySelector('.cart-drawer-contents');
      if (contents) {
        contents.innerHTML = html;
      }
    } catch (error) {
      console.error('Refresh cart error:', error);
    }
  }

  async updateQuantity(input) {
    const line = input.dataset.line;
    const quantity = parseInt(input.value);

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      await this.refreshCart();
    } catch (error) {
      console.error('Update quantity error:', error);
    }
  }

  async removeItem(button) {
    const line = button.dataset.line;
    
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity: 0 })
      });
      await this.refreshCart();
    } catch (error) {
      console.error('Remove item error:', error);
    }
  }
}

// ===========================================
// 8. PRODUCT VARIANT SELECTOR
// ===========================================
class VariantSelector {
  constructor() {
    this.init();
  }

  init() {
    const variantSelectors = document.querySelectorAll('.variant-selector');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', this.handleVariantChange.bind(this));
    });
  }

  handleVariantChange(e) {
    const form = e.target.closest('form');
    const selectedOptions = Array.from(form.querySelectorAll('.variant-selector'))
      .map(select => select.value);

    const variant = this.findVariant(selectedOptions);
    
    if (variant) {
      this.updatePrice(form, variant);
      this.updateAvailability(form, variant);
      this.updateImages(variant);
    }
  }

  findVariant(selectedOptions) {
    // This would need product JSON data
    // Implementation depends on your theme structure
    return null;
  }

  updatePrice(form, variant) {
    const priceElement = form.querySelector('.product-price');
    if (priceElement && variant.price) {
      priceElement.textContent = this.formatPrice(variant.price);
    }
  }

  updateAvailability(form, variant) {
    const button = form.querySelector('.add-to-cart-btn');
    if (button) {
      if (variant.available) {
        button.disabled = false;
        button.textContent = 'Add to Cart';
      } else {
        button.disabled = true;
        button.textContent = 'Sold Out';
      }
    }
  }

  updateImages(variant) {
    if (variant.featured_image) {
      const mainImage = document.querySelector('.product-main-image');
      if (mainImage) {
        mainImage.src = variant.featured_image.src;
      }
    }
  }

  formatPrice(price) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(price / 100);
  }
}

// ===========================================
// INITIALIZE ALL FEATURES
// ===========================================
document.addEventListener('DOMContentLoaded', () => {
  new QuickAddToCart();
  new QuickView();
  new LazyLoadImages();
  new StickyHeader();
  new SearchAutocomplete();
  new RecentlyViewed();
  new CartDrawer();
  new VariantSelector();

  console.log('✅ All optimization features loaded');
});